/**
 * shareEngine.js — Share card data generation and canvas rendering.
 *
 * Pure functions — zero React.
 * Generates status-positive card data (NO balances) and draws
 * premium cards to canvas for PNG download.
 *
 * Card types:
 *   "trajectory" — Growth multiple, FI age, savings rate, plan duration
 *   "quest"      — Completed quest with icon, title, and reward
 *   "streak"     — Streak count and tier
 */

import { QUESTS } from '../quests/questConfig';
import { getStreakTier, STREAK_DEFAULTS } from '../streaks/streakEngine';

/* ═══════════════════════════════════════════════════
   CARD DATA GENERATORS
   ═══════════════════════════════════════════════════ */

/**
 * Build data for the Wealth Trajectory card.
 * Status-positive: shows multiples and milestones, NOT dollar amounts.
 */
export function trajectoryCardData(s, proj, pF, fiAge, fiReached) {
  const years = (s.maxAge || 40) - s.age;
  const growth = Math.round(pF.nw / Math.max(1, s.nw0));
  const w = proj.find((d) => d.a >= s.startAge);
  const sr = w && w.comp > 0 ? Math.round((w.sv / w.comp) * 100) : 0;
  const questsDone = Object.keys(s.questsCompleted || {}).length;

  return {
    type: "trajectory",
    headline: `${years}-Year Wealth Plan`,
    stats: [
      { label: "Growth", value: `${growth}×`, sub: "projected multiplier" },
      { label: "FI Age", value: fiReached ? `${fiAge}` : `${s.maxAge || 40}+`, sub: fiReached ? "independence" : "target age" },
      { label: "Save Rate", value: `${sr}%`, sub: "of income" },
      { label: "Quests", value: `${questsDone}/${QUESTS.length}`, sub: "complete" },
    ],
    tagline: fiReached
      ? `On track for financial independence by ${fiAge}.`
      : `Building a ${years}-year wealth plan.`,
  };
}

/**
 * Build data for a Quest Achievement card.
 */
export function questCardData(questId, s) {
  const quest = QUESTS.find((q) => q.id === questId);
  if (!quest) return null;

  const completedAt = s.questsCompleted?.[questId];
  const date = completedAt
    ? new Date(completedAt).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" })
    : "Today";

  return {
    type: "quest",
    icon: quest.icon,
    title: quest.title,
    reward: quest.reward,
    category: quest.category,
    date,
  };
}

/**
 * Build data for the Streak card.
 */
export function streakCardData(s) {
  const streak = s.streak || STREAK_DEFAULTS;
  const { tier, emoji } = getStreakTier(streak.current);

  return {
    type: "streak",
    count: streak.current,
    best: streak.best,
    tier,
    emoji,
    tagline: streak.current >= 30
      ? "Consistency is my competitive advantage."
      : streak.current >= 7
        ? "Building wealth, one day at a time."
        : "The journey of a thousand miles starts here.",
  };
}


/* ═══════════════════════════════════════════════════
   CANVAS RENDERER — draws cards as downloadable PNGs
   ═══════════════════════════════════════════════════ */

const CARD_W = 600;
const CARD_H = 400;
const DPR = 2; // 2× for retina

/**
 * Draw a share card to a canvas and return a download URL.
 *
 * @param {object} cardData — from one of the data generators above
 * @param {boolean} dark — dark mode flag
 * @returns {string} — blob URL for the PNG image
 */
export function renderCardToBlob(cardData, dark) {
  const canvas = document.createElement("canvas");
  canvas.width = CARD_W * DPR;
  canvas.height = CARD_H * DPR;
  const ctx = canvas.getContext("2d");
  ctx.scale(DPR, DPR);

  // Colors
  const bg = dark ? "#0A0A0A" : "#FFFFFF";
  const fg = dark ? "#F5F5F7" : "#1D1D1F";
  const muted = dark ? "#98989D" : "#6E6E73";
  const blue = "#0A84FF";
  const green = "#30D158";
  const border = dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";

  // Background with subtle gradient
  const grad = ctx.createLinearGradient(0, 0, CARD_W, CARD_H);
  if (dark) {
    grad.addColorStop(0, "#0D0D10");
    grad.addColorStop(0.5, "#0A0A0A");
    grad.addColorStop(1, "#111114");
  } else {
    grad.addColorStop(0, "#FFFFFF");
    grad.addColorStop(0.5, "#F8F9FF");
    grad.addColorStop(1, "#F2F2F7");
  }
  roundRect(ctx, 0, 0, CARD_W, CARD_H, 24);
  ctx.fillStyle = grad;
  ctx.fill();

  // Border
  roundRect(ctx, 0.5, 0.5, CARD_W - 1, CARD_H - 1, 24);
  ctx.strokeStyle = border;
  ctx.lineWidth = 1;
  ctx.stroke();

  // Branding
  ctx.fillStyle = fg;
  ctx.font = "800 14px -apple-system, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif";
  ctx.fillText("Wealthmaxxing", 36, 44);
  ctx.fillStyle = blue;
  ctx.font = "700 9px -apple-system, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif";
  ctx.letterSpacing = "1.2px";
  ctx.fillText("PLAN OS", 36, 58);
  ctx.letterSpacing = "0px";

  // Dispatch to card type renderer
  if (cardData.type === "trajectory") {
    drawTrajectoryCard(ctx, cardData, { fg, muted, blue, green, dark });
  } else if (cardData.type === "quest") {
    drawQuestCard(ctx, cardData, { fg, muted, blue, green, dark });
  } else if (cardData.type === "streak") {
    drawStreakCard(ctx, cardData, { fg, muted, blue, green, dark });
  }

  // Footer
  ctx.fillStyle = muted;
  ctx.font = "500 10px -apple-system, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif";
  ctx.globalAlpha = 0.5;
  ctx.fillText("wealthmaxxing.app", 36, CARD_H - 24);
  ctx.globalAlpha = 1;

  return canvas.toDataURL("image/png");
}

/* ─── Trajectory card ─── */
function drawTrajectoryCard(ctx, data, cl) {
  // Headline
  ctx.fillStyle = cl.fg;
  ctx.font = "800 36px -apple-system, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif";
  ctx.fillText(data.headline, 36, 115);

  // Tagline
  ctx.fillStyle = cl.muted;
  ctx.font = "500 14px -apple-system, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif";
  ctx.fillText(data.tagline, 36, 145);

  // Stats grid (2×2)
  const startY = 180;
  const colW = 260;
  const rowH = 85;

  data.stats.forEach((stat, i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const x = 36 + col * colW;
    const y = startY + row * rowH;

    // Stat value
    ctx.fillStyle = i === 0 ? cl.green : i === 1 ? cl.blue : cl.fg;
    ctx.font = "800 32px -apple-system, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif";
    ctx.fillText(stat.value, x, y + 30);

    // Label
    ctx.fillStyle = cl.muted;
    ctx.font = "600 10px -apple-system, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif";
    ctx.letterSpacing = "1px";
    ctx.fillText(stat.label.toUpperCase(), x, y + 6);
    ctx.letterSpacing = "0px";

    // Sub
    ctx.font = "500 11px -apple-system, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif";
    ctx.fillText(stat.sub, x, y + 48);
  });
}

/* ─── Quest card ─── */
function drawQuestCard(ctx, data, cl) {
  const catColors = {
    safety: "#FF9F0A", credit: "#BF5AF2", budget: "#30D158",
    invest: "#0A84FF", streak: "#FF375F",
  };
  const catColor = catColors[data.category] || cl.blue;

  // Icon (large emoji)
  ctx.font = "56px -apple-system, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif";
  ctx.fillText(data.icon, 36, 135);

  // Title
  ctx.fillStyle = cl.fg;
  ctx.font = "800 28px -apple-system, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif";
  ctx.fillText(data.title, 36, 195);

  // Category badge
  ctx.fillStyle = catColor;
  ctx.font = "700 10px -apple-system, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif";
  ctx.letterSpacing = "1px";
  ctx.fillText(data.category.toUpperCase(), 36, 220);
  ctx.letterSpacing = "0px";

  // Reward message
  ctx.fillStyle = cl.muted;
  ctx.font = "500 15px -apple-system, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif";
  wrapText(ctx, `"${data.reward}"`, 36, 260, CARD_W - 72, 22);

  // Date
  ctx.fillStyle = cl.muted;
  ctx.globalAlpha = 0.5;
  ctx.font = "500 11px -apple-system, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif";
  ctx.fillText(`Achieved ${data.date}`, 36, 330);
  ctx.globalAlpha = 1;
}

/* ─── Streak card ─── */
function drawStreakCard(ctx, data, cl) {
  const streakColor = data.tier === "legendary" ? "#BF5AF2"
    : data.tier === "elite" ? "#FF9F0A"
    : data.tier === "dedicated" ? cl.blue
    : cl.green;

  // Big number
  ctx.fillStyle = streakColor;
  ctx.font = "800 96px -apple-system, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif";
  ctx.fillText(`${data.count}`, 36, 185);

  // "Day Streak" label
  ctx.fillStyle = cl.fg;
  ctx.font = "600 20px -apple-system, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif";
  ctx.fillText(`Day Streak ${data.emoji}`, 36, 220);

  // Tier label
  ctx.fillStyle = streakColor;
  ctx.font = "700 10px -apple-system, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif";
  ctx.letterSpacing = "1.5px";
  ctx.fillText(data.tier.toUpperCase(), 36, 250);
  ctx.letterSpacing = "0px";

  // Tagline
  ctx.fillStyle = cl.muted;
  ctx.font = "500 14px -apple-system, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif";
  ctx.fillText(data.tagline, 36, 290);

  // Best streak
  if (data.best > data.count) {
    ctx.fillStyle = cl.muted;
    ctx.globalAlpha = 0.5;
    ctx.font = "500 12px -apple-system, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif";
    ctx.fillText(`Personal best: ${data.best} days`, 36, 320);
    ctx.globalAlpha = 1;
  }
}


/* ═══════════════════════════════════════════════════
   UTILITY
   ═══════════════════════════════════════════════════ */

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function wrapText(ctx, text, x, y, maxW, lineH) {
  const words = text.split(" ");
  let line = "";
  let cy = y;
  for (const word of words) {
    const test = line + word + " ";
    if (ctx.measureText(test).width > maxW && line.length > 0) {
      ctx.fillText(line.trim(), x, cy);
      line = word + " ";
      cy += lineH;
    } else {
      line = test;
    }
  }
  ctx.fillText(line.trim(), x, cy);
}

/**
 * Trigger a PNG download from a data URL.
 */
export function downloadCard(dataUrl, filename) {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename || "wealthmaxx-card.png";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
